import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useMemo, type ReactNode } from 'react'
import {
  Box,
  Table,
  Tbody,
  Tr,
  Td,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { TableHeader, type TableHeaderRef } from './TableHeader'
import { TableBody, type TableBodyRef } from './TableBody'
import { Pagination } from './Pagination'
import { RowContextMenu, CLOSED_ROW_CONTEXT_MENU, type RowContextMenuState } from './RowContextMenu'
import { RowActionHoverHint, CLOSED_ROW_HOVER_HINT, type RowHoverHintState } from './RowActionHoverHint'
import type { DataTableOptions, Permission, SortParams, MetaData, PaginationLink } from './types'
import { getTableColumnCount, shouldShowActionsColumn } from './types'

/** Build PaginationLink[] from Laravel root-level links (first, last, prev, next) when meta.links is missing */
function linksFromLaravelFormat(data: { links?: { first?: string | null; last?: string | null; prev?: string | null; next?: string | null }; meta?: { current_page?: number; last_page?: number } }): PaginationLink[] {
  const rootLinks = data.links
  const current = data.meta?.current_page ?? 1
  const lastPage = Math.max(1, data.meta?.last_page ?? 1)
  if (!rootLinks || typeof rootLinks !== 'object') return []
  const links: PaginationLink[] = [
    { url: rootLinks.prev ?? null, label: '&laquo; Previous', active: false },
  ]
  const firstUrl = rootLinks.first ?? null
  const pageUrl = (i: number) => {
    if (!firstUrl) return null
    if (i === 1) return firstUrl
    return firstUrl.replace(/([?&])page=\d+/, `$1page=${i}`) || `${firstUrl}${firstUrl.includes('?') ? '&' : '?'}page=${i}`
  }
  const start = Math.max(1, current - 2)
  const end = Math.min(lastPage, current + 2)
  for (let i = start; i <= end; i++) {
    links.push({ url: pageUrl(i), label: String(i), active: i === current })
  }
  links.push({ url: rootLinks.next ?? null, label: 'Next &raquo;', active: false })
  return links
}
import { useThemeColors } from '@/hooks/useThemeColors'

interface DataTableProps {
  options: DataTableOptions
  params?: Record<string, any>
  onOpenModal?: (id: any) => void
  setSubSub?: (all: number, trashed: number) => void
  access?: Permission
  filterOptions?: Record<string, Array<{ value: string | number; label: string }>>
  customRenderers?: Record<string, (value: any, row?: any) => ReactNode>
  actionLoadingStates?: Record<string | number, boolean> // Map of row id to loading state
  customActionRenderer?: (row: any, isLoading?: boolean) => ReactNode // Custom renderer for actions column
  onDataLoad?: (data: any[], meta?: MetaData) => void // Callback when data is loaded
  getRowPermissions?: (row: any) => Permission // Per-row override; when can_edit/can_delete false, action is hidden for that row
}

export interface DataTableRef {
  getSelectedRows: () => number[]
  clearPage: () => void
  /** Clear table rows and total so the table shows empty before refetch (e.g. on tab change). */
  clearData: () => void
  reload: () => void
  applyFilter: (field: string, value: any) => void
}

export const DataTable = forwardRef<DataTableRef, DataTableProps>(
  ({ options, params, onOpenModal, setSubSub, access, filterOptions, customRenderers, actionLoadingStates, customActionRenderer, onDataLoad, getRowPermissions }, ref) => {
    const { showError, showSuccess } = useToastNotification()
    const [sortParams, setSortParams] = useState<SortParams>({ order: '', sort: 'asc' })
    const [isLoading, setIsLoading] = useState(false)
    const [dataRows, setDataRows] = useState<any[]>([])
    const [totalRows, setTotalRows] = useState<number | null>(null)
    const [metaData, setMetaData] = useState<MetaData | null>(null)
    const [nextPage, setNextPage] = useState<string | null>(null)
    const [toDelete, setToDelete] = useState<any>(null)
    const [toRestore, setToRestore] = useState<any>(null)
    const [filters, setFilters] = useState<Record<string, any>>({})
    const [rowContextMenu, setRowContextMenu] = useState<RowContextMenuState>(CLOSED_ROW_CONTEXT_MENU)
    const [rowHoverHint, setRowHoverHint] = useState<RowHoverHintState>(CLOSED_ROW_HOVER_HINT)

    const theadRef = useRef<TableHeaderRef>(null)
    const tbodyRef = useRef<TableBodyRef>(null)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const columnCount = getTableColumnCount(options, !!customActionRenderer)
    const showActionsColumn = shouldShowActionsColumn(options, !!customActionRenderer)
    const useRowContextMenu = !!options.useRowContextMenu && !customActionRenderer

    const closeRowContextMenu = () => setRowContextMenu(CLOSED_ROW_CONTEXT_MENU)
    const hideRowHoverHint = () => setRowHoverHint(CLOSED_ROW_HOVER_HINT)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [modalDescription, setModalDescription] = useState('')

    const colors = useThemeColors()
    const spinnerColor = 'blue.500'
    const loadingTextColor = colors.text
    const emptyTextColor = colors.textSecondary
    const tableBg = colors.cardBg
    const borderColor = colors.border
    const scrollbarThumb = colors.progressBg
    const scrollbarThumbHover = colors.textMuted
    const theadBg = colors.cardBg

    const mergedDataFields = useMemo(() => {
      if (!filterOptions) return options.dataFields

      const merged = { ...options.dataFields }
      Object.keys(filterOptions).forEach((field) => {
        if (merged[field]) {
          merged[field] = {
            ...merged[field],
            withFilter: true,
            filterOptions: filterOptions[field],
          }
        }
      })
      return merged
    }, [options.dataFields, filterOptions])

    const handleSort = (field: string, sort: 'asc' | 'desc') => {
      setSortParams({
        order: field,
        sort,
      })
    }

    const handleFilter = (field: string, value: any) => {
      setFilters((prev) => {
        if (value === null || value === undefined || value === '') {
          const newFilters = { ...prev }
          delete newFilters[field]
          return newFilters
        }
        return {
          ...prev,
          [field]: value,
        }
      })
      setNextPage(null)
    }

    const handleAction = (id: any, action: string) => {
      setToRestore(null)
      setToDelete(null)

      if (action === 'Edit' && options?.displayInModal) {
        onOpenModal?.(id)
        return
      }

      const actionMessages: Record<string, string> = {
        Restore: 'Do you really want to restore this record?',
        Trash: 'Do you really want to delete this record?',
        Delete: 'You are about to permanently delete these items from your site. This action cannot be undone.',
        BulkDelete: 'You are about to permanently delete multiple items from your site. This action cannot be undone.',
        BulkRestore: 'Do you really want to restore multiple records?',
      }

      if (actionMessages[action]) {
        setToRestore(action === 'Restore' || action === 'BulkRestore' ? id : null)
        setToDelete(action === 'Trash' || action === 'Delete' || action === 'BulkDelete' ? id : null)
        setModalDescription(actionMessages[action])
        onOpen()
      }
    }

    const onValidate = async () => {
      try {
        if (toDelete) {
          const url = `${options.dataSource}/${toDelete}`
          await apiClient.delete(url)
          showSuccess('Success', 'Record deleted successfully')
          setNextPage(null)
          getDataSource()
        }

        if (toRestore) {
          // Restore endpoint is on the base resource path, not on /archived (e.g. /service-catalog/service-types/restore/3)
          const basePath = options.dataSource.replace(/\/archived\/?$/, '')
          const url = `${basePath}/restore/${toRestore}`
          await apiClient.patch(url)
          showSuccess('Success', 'Record restored successfully')
          setNextPage(null)
          getDataSource()
        }
      } catch (error: any) {
        showError(error)
      } finally {
        onClose()
        setToDelete(null)
        setToRestore(null)
      }
    }

    const getDataSource = async () => {
      setIsLoading(true)
      try {
        const dataSource = nextPage || options.dataSource
        const sortForRequest = sortParams.order
          ? { order: sortParams.order, sort: sortParams.sort }
          : {}
        const requestParams = {
          ...params,
          ...sortForRequest,
          ...filters,
        }

        const { data } = await apiClient.get(dataSource, { params: requestParams })

        if (data.data) {
          const meta = data.meta || {
            total: data.total ?? 0,
            per_page: data.per_page ?? data.data.length,
            current_page: data.current_page ?? 1,
            last_page: data.last_page ?? 1,
            from: data.from ?? null,
            to: data.to ?? null,
            links: data.links ?? [],
            path: data.path ?? '',
            first_page_url: data.first_page_url ?? null,
            last_page_url: data.last_page_url ?? null,
            next_page_url: data.next_page_url ?? null,
            prev_page_url: data.prev_page_url ?? null,
          }
          if ((!meta.links || !Array.isArray(meta.links) || meta.links.length === 0) && data.links && typeof data.links === 'object') {
            meta.links = linksFromLaravelFormat(data)
          }

          setTotalRows(meta.total ?? 0)
          if (options.softDelete) {
            setSubSub?.(meta.all || 0, meta.trashed || 0)
          }
          setDataRows(data.data)
          setMetaData(meta)
          onDataLoad?.(data.data, meta)
        } else {
          setTotalRows(data.length)
          setDataRows(data)
          const meta = {
            total: data.length,
            per_page: data.length,
            current_page: 1,
            links: [],
          } as MetaData
          setMetaData(meta)
          onDataLoad?.(data, meta)
        }

        theadRef.current?.setCheckedAll(false)
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        getDataSource()
      }, 300)

      return () => clearTimeout(timeoutId)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, options.dataSource, sortParams, nextPage, filters])

    useEffect(() => {
      setNextPage(null)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, filters])

    useImperativeHandle(ref, () => ({
      getSelectedRows() {
        return tbodyRef.current?.getSelectedData() || []
      },
      clearPage() {
        setNextPage(null)
      },
      clearData() {
        setDataRows([])
        setTotalRows(0)
        setMetaData(null)
        setNextPage(null)
        theadRef.current?.setCheckedAll(false)
      },
      reload() {
        setDataRows([])
        getDataSource()
        theadRef.current?.setCheckedAll(false)
      },
      applyFilter(field: string, value: any) {
        handleFilter(field, value)
      },
    }))

    const renderTableContent = () => {
      if (isLoading) {
        return (
          <Tbody>
            <Tr>
              <Td colSpan={columnCount} textAlign="center" py={8}>
                <VStack spacing={2}>
                  <Spinner size="lg" color={spinnerColor} />
                  <Text color={loadingTextColor}>Loading...</Text>
                </VStack>
              </Td>
            </Tr>
          </Tbody>
        )
      }

      if (totalRows === 0 || dataRows.length === 0) {
        return (
          <Tbody>
            <Tr>
              <Td colSpan={columnCount} textAlign="center" py={8}>
                <Text color={emptyTextColor}>{options.noRecordText || 'No Record Found'}</Text>
              </Td>
            </Tr>
          </Tbody>
        )
      }

      return (
        <TableBody
          options={options}
          rows={dataRows}
          ref={tbodyRef}
          permissions={access}
          getRowPermissions={getRowPermissions}
          onAction={handleAction}
          onCheckedAll={(checked) => theadRef.current?.setCheckedAll(checked)}
          customRenderers={customRenderers}
          actionLoadingStates={actionLoadingStates}
          customActionRenderer={customActionRenderer}
          onRowContextMenu={
            useRowContextMenu
              ? (state) => {
                  hideRowHoverHint()
                  setRowContextMenu(state)
                }
              : undefined
          }
          onRowHoverHint={useRowContextMenu ? setRowHoverHint : undefined}
        />
      )
    }

    const renderPagination = () => {
      if (!isLoading && metaData && metaData.total > 0 && metaData.links && metaData.links.length > 0) {
        return <Pagination metas={metaData} onClick={setNextPage} />
      }
      return null
    }

    const pagination = renderPagination()

    return (
      <Box display="flex" flexDirection="column" h="100%" minH={0} position="relative" p={0} border="1px" borderColor={borderColor} borderRadius="md" bg={tableBg}>
        <Box
          ref={scrollContainerRef}
          flex="1"
          minH={0}
          overflowX="auto"
          overflowY="auto"
          position="relative"
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: scrollbarThumb,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: scrollbarThumbHover,
            },
            'thead th': {
              position: 'sticky',
              top: 0,
              zIndex: 20,
              backgroundColor: `${theadBg} !important`,
              boxShadow: `inset 0 -1px 0 ${borderColor}`,
            },
            table: {
              borderCollapse: 'separate',
              borderSpacing: 0,
            },
          }}
        >
          <Table variant="simple" colorScheme="gray" size="sm" w="100%" minW="max-content" sx={{ tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHeader
              header={mergedDataFields}
              onCheckAll={(checked) => tbodyRef.current?.checkedAll(checked)}
              onSort={handleSort}
              onFilter={handleFilter}
              activeFilters={filters}
              ref={theadRef}
              showActionsColumn={showActionsColumn}
              hideSelection={options.hideSelection}
            />
            {renderTableContent()}
          </Table>
          {useRowContextMenu && (
            <>
              <RowActionHoverHint
                hintState={rowHoverHint}
                scrollContainerRef={scrollContainerRef}
                onHide={hideRowHoverHint}
              />
              <RowContextMenu
                menuState={rowContextMenu}
                onClose={closeRowContextMenu}
                scrollContainerRef={scrollContainerRef}
              />
            </>
          )}
        </Box>

        {pagination ? (
          <Box flexShrink={0} px={6} pt={4} pb={4} borderTop="1px" borderColor={borderColor} bg={tableBg} position="relative" zIndex={5}>
            {pagination}
          </Box>
        ) : (
          <Box flexShrink={0} px={6} pt={4} pb={4} borderTop="1px" borderColor={borderColor} bg={tableBg} position="relative" zIndex={5} minH="60px" />
        )}

        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Confirmation</ModalHeader>
            <ModalBody>
              <Text>{modalDescription}</Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={onValidate}>
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    )
  }
)

DataTable.displayName = 'DataTable'


